/**
 * comment-list.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Observable } from "rxjs/Rx";

import { IntakeStep } from "../algorithms/structure/intake-step";
import { Comment, Visibility } from "./comment";
import { CommentService } from "./comment.service";

interface VisibilityOption
{
  value : Visibility;
  label : string;
}

@Component({
  selector: "comment-list",
  template: `
  <form [formGroup]="form">
  <template [ngIf]="showComments">
    <div *ngFor="let comment of _comments; let i = index" class="comment-block">
      <!-- Upper labels (author, group) -->
      <div class="comment-labels">
        <strong>{{comment.creator}}</strong>
        <span> [{{comment.org}}]</span>
        <span class="pull-right"></span>
      </div>
      <!-- Comment body -->
      <div
        *ngIf="!comment.editing"
        class="comment autohyphen"
        [class.bg-danger]="comment.public"
        [class.bg-success]="!comment.groupIds?.length"
        >{{comment.comment}}</div>
      <textarea
        *ngIf="comment.editing"
        class="comment"
        [class.bg-danger]="comment.public"
        [formControlName]="'c' + comment.commentId"
        (blur)="edit(comment)">
      </textarea>
      <!-- Lower labels (visibility, buttons, timestamp) -->
      <div class="text-right comment-labels">
        <small class="visibility">
          <span *ngIf="!smaller">Visibility:</span>
          <select [formControl]="comment.visControl">
            <option *ngFor="let opt of visibilityOptions" [ngValue]="opt.value">
              {{opt.label}}
            </option>
          </select>
        </small>
        <span
          *ngIf="comment.editable && (!editingAny || comment.editing)"
          class="comment-edits">
          <a
            *ngIf="!comment.editing"
            (click)="edit(comment)">
            <span *ngIf="!smaller">
              {{comment.editing ? "(Editing)" : "Edit"}}
            </span>
            <span
              *ngIf="smaller"
              class="glyphicon"
              [class.glyphicon-pencil]="!comment.editing"
              [class.glyphicon-option-horizontal]="comment.editing">
            </span>
          </a>
          <a (click)="delete(comment)" class="text-danger">
            <span *ngIf="!smaller">Delete</span>
            <span
              *ngIf="smaller"
              class="glyphicon glyphicon-trash">
            </span>
          </a>
        </span>
        <br *ngIf="smaller">
        {{comment.created | date:'short'}}
        <strong *ngIf="comment.created !== comment.updated">*</strong>
      </div>
    </div>
  </template>

  <!-- New comment form field -->
  <div class="new-comment" *ngIf="showNewForm">
    <textarea
      class="form-control"
      formControlName="newComment"
      placeholder="New comment"
      tabindex="-1"
      (blur)="addComment()">
    </textarea>
    <table *ngIf="!smaller">
      <tr>
        <td style="width: 80px">
          <label>Visibility:</label>
        </td>
        <td>
          <select
            formControlName="newVisibility"
            class="form-control"
            tabindex="-1">
            <option *ngFor="let opt of visibilityOptions" [ngValue]="opt.value">
              {{opt.label}}
            </option>
          </select>
        </td>
        <!--<td>
          <button
            class="btn btn-primary form-control"
            (click)="addComment()"
            tabindex="-1"
            [disabled]="editingAny">
            Add
          </button>
        </td>-->
      </tr>
    </table>
    <template [ngIf]="smaller">
      <select
        formControlName="newVisibility"
        class="form-control"
        tabindex="-1">
        <option *ngFor="let opt of visibilityOptions" [ngValue]="opt.value">
          {{opt.label}}
        </option>
      </select>
      <button
          class="btn btn-primary form-control"
          (click)="addComment()"
          tabindex="-1">
          Add
      </button>
    </template>
  </div>
  </form>
`,
  styles: [
    `
    .comment-labels>* {
      padding: 5px;
    }
    .comment-labels .pull-right {
      padding-top: 0px;
    }
    .comment {
      white-space: pre-wrap; /* preserve newlines/etc in comment context */
      padding: 10px;
      background-color: #f5f5f5;
      border-radius: 10px;
      width: 100%;
    }
    textarea.comment {
      resize: vertical;
    }
    .comment.bg-danger {
      background-color: #f2dede;
    }
    .comment.bg-success {
      background-color: #dff0d8;
    }
    .comment-block {
      margin-bottom: 1em;
    }
    .new-comment {
      margin-bottom: 1em;
    }
    .new-comment textarea {
      border-radius: 10px;
      background-color: #f5f5f5;
      resize: vertical;
    }
    table {
      width: 100%;
      text-align: right;
      vertical-align: middle;
    }
    table td {
      padding: 5px;
    }
    table label {
      margin-bottom: 0;
    }
    `
  ]
})
export class CommentListComponent implements OnInit, OnDestroy
{
  /**
   * The (true, internal) list of comments this component is responsible for
   * showing and editing.
   */
  public _comments : Comment[];

  @Input()
  public showNewForm : boolean = true;

  @Input()
  public showComments : boolean = true;

  @Input()
  public set comments (c : Comment[])
  {
    if (!Array.isArray(c))
    {
      this._comments = [];
      return;
    }
    this._comments = c.sort(
      (a : Comment, b : Comment) => a.created - b.created);
    this.generateVisibilityControls();
  }

  private generateVisibilityControls () : void
  {
    this._comments.forEach((c : Comment) => {
      c.visControl = new FormControl(
        {value: this.visibilityFor(c), disabled: !c.editable});
      c.visControl.valueChanges.subscribe(
        (v : Visibility) => this.changeVisibility(c, v));
    });
  }

  @Input()
  public parent : IntakeStep | null;

  @Input()
  public smaller : boolean = false;

  /**
   * The control for the comment text in the new comment form.
   */
  public newComment : FormControl =
    new FormControl("", Validators.required);

  /**
   * The control for the visibility selector in the new comment form.
   */
  public newVisibility : FormControl =
    new FormControl(Visibility.GroupOnly, Validators.required);

  /**
   * A form array to hold controls for modifying the visibility of each existing
   * comment.
   */
  public visibilityControls : FormControl[] = [];

  /**
   * The selectable options for visibility settings, used by the dropdowns to
   * build their <option> elements.
   */
  public readonly visibilityOptions : VisibilityOption[] = [
    {value: Visibility.Public, label: "Public"},
    {value: Visibility.GroupOnly, label: "My Groups"},
    {value: Visibility.SelfOnly, label: "Only Me"}
  ];

  /**
   * The overall form object. This is needed to embed the controls within the
   * "superform" for all comments that aggregates their current clean/dirty
   * status for determining save-required on the intake form.
   */
  public form : FormGroup;

  /**
   * Name to attach this comment list under in the comment "superform".
   */
  private registeredName : string;

  constructor (private commentService : CommentService) {/* DI */}

  public ngOnInit () : void
  {
    this.form = new FormGroup({
      newComment: this.newComment,
      newVisibility: this.newVisibility
    });

    this.registeredName =
      ((this.parent instanceof IntakeStep) ? `${this.parent.id}` : "context");
    this.commentService.globalForm.addControl(this.registeredName, this.form);
  }

  public ngOnDestroy () : void
  {
    this.commentService.globalForm.removeControl(this.registeredName);
  }

  public addComment () : void
  {
    if (this.newComment.invalid)
    {
      // alert("You cannot submit a blank comment.");
      return;
    }
    if (this.newVisibility.invalid)
    {
      return alert("You must select a visibility level for the comment.");
    }
    const text : string = this.newComment.value;
    const vis : Visibility = this.newVisibility.value;
    console.log("adding with vis", vis, (typeof vis));

    const commentResult : Observable<boolean> =
      (this.parent instanceof IntakeStep)
        ? this.commentService.addForStep(this.parent.id, text, vis)
        : this.commentService.addForSubject(text, vis);

    commentResult.subscribe((added : boolean) => {
      if (!added)
      {
        // TODO: user-visible error
        console.error("Unable to post comment!");
      }
      else
      {
        this.newComment.reset();
        this.newVisibility.reset(Visibility.GroupOnly);
      }
    });
  }

  public editingAny : boolean = false;

  public edit (comment : Comment) : void
  {
    if (!comment.editing)
    {
      if (this.editingAny)
      {
        return;
        // due to the nature of comment responses, we can't actually keep a
        // comment in the editing state after receiving a response (since it
        // clobbers the whole list), so allowing multiple edits open at once
        // just invites users to see the clobbering happen (which is confusing)
      }
      // activate edit mode
      this.createControl(comment);
      comment.editing = true;
      this.editingAny = true;
      return;
    }

    const control : FormControl =
      this.form.controls["c" + comment.commentId] as FormControl;
    if (control.invalid)
    {
      return alert("You cannot submit a blank comment.");
    }
    const updatedComment : Comment = Object.assign(
      new Comment(),
      comment,
      {comment: `${control.value}`}
    );

    const updateResult : Observable<boolean> =
      (this.parent instanceof IntakeStep)
        ? this.commentService.updateForStep(this.parent.id, updatedComment)
        : this.commentService.updateForSubject(updatedComment);

    updateResult.subscribe((updated : boolean) => {
      if (updated)
      {
        comment.editing = false;
        control.markAsPristine();
        this.editingAny = this._comments.some((c : Comment) => c.editing);
      }
      else
      {
        // TODO
        console.error("Unable to edit comment!");
      }
    });
  }

  public changeVisibility (comment : Comment, vis : Visibility) : void
  {
    console.log(comment);
    // const vis : Visibility = comment.visControl.value;
    console.log(vis, (typeof vis));

    const toggleResult : Observable<boolean> =
      (this.parent instanceof IntakeStep)
        ? this.commentService.updateForStep(this.parent.id, comment, vis)
        : this.commentService.updateForSubject(comment, vis);

    toggleResult.subscribe(
      (updated : boolean) => {
        if (!updated)
        {
          // TODO
          console.error("Unable to change comment visitbility!");
        }
      }
    );
  }

  public delete (comment : Comment) : void
  {
    const deleteResult : Observable<boolean> =
      (this.parent instanceof IntakeStep)
        ? this.commentService.removeForStep(this.parent.id, comment)
        : this.commentService.removeForSubject(comment);
    deleteResult.subscribe(
      (deleted : boolean) => {
        if (!deleted)
        {
          console.error("Unable to delete comment!");
        }
      }
    );
  }

  private createControl (comment : Comment) : void
  {
    this.form.setControl(
      "c" + comment.commentId,
      new FormControl(comment.comment, Validators.required)
    );
  }

  public visibilityFor (comment : Comment) : Visibility
  {
    return Comment.visibilityOf(comment);
  }

}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/intake-form/comment-list.component.ts